/**
 * Read a plain Edit snapshot back out of the Y.Doc.
 *
 * Ordering rules (must mirror what the format derives / authors):
 *  - tracks: by fractional `ord`, id tiebreak
 *  - clips within a track: by `start`, id tiebreak (same rule as sortClips
 *    in format/invariants.ts — array order carries no authored information)
 *  - effects within a clip: by fractional `ord`, id tiebreak
 *
 * Returned objects share leaf references with the doc's stored values; both
 * sides treat snapshots as immutable (the store replaces leaves wholesale),
 * so no defensive cloning happens here.
 */
import * as Y from 'yjs';
import { emptyEdit } from '../format/helpers';
import type { AnyClip, Edit, EditSettings, Effect, Source, Track } from '../format/types';
import { PARAM_MAP_KEYS, ROOT_META, ROOT_SOURCES, ROOT_TRACKS } from './schema';

type YMap = Y.Map<unknown>;

interface EffectEntry {
  ord: string;
  effect: Omit<Effect, 'id'>;
}

function byOrdThenId(a: { ord: string; id: string }, b: { ord: string; id: string }): number {
  return a.ord < b.ord ? -1 : a.ord > b.ord ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function matParamMap(m: YMap): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of m.entries()) out[k] = v;
  return out;
}

function matEffects(m: YMap): Effect[] {
  const entries: { id: string; ord: string; effect: Omit<Effect, 'id'> }[] = [];
  for (const [id, v] of m.entries()) {
    const e = v as EffectEntry;
    entries.push({ id, ord: e.ord, effect: e.effect });
  }
  entries.sort(byOrdThenId);
  return entries.map((e) => ({ id: e.id, ...e.effect }) as Effect);
}

function matClip(id: string, m: YMap): AnyClip {
  const out: Record<string, unknown> = { id };
  for (const [k, v] of m.entries()) {
    if (k === 'effects') out[k] = matEffects(v as YMap);
    else if (PARAM_MAP_KEYS.has(k)) out[k] = matParamMap(v as YMap);
    else out[k] = v;
  }
  return out as unknown as AnyClip;
}

function matTrack(id: string, m: YMap): { ord: string; track: Track } {
  const out: Record<string, unknown> = { id };
  let ord = '';
  let clips: AnyClip[] = [];
  for (const [k, v] of m.entries()) {
    if (k === 'ord') ord = v as string;
    else if (k === 'clips') {
      clips = [...(v as YMap).entries()].map(([cid, cm]) => matClip(cid, cm as YMap));
    } else out[k] = v;
  }
  clips.sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
  out.clips = clips;
  return { ord, track: out as unknown as Track };
}

export function materializeEdit(doc: Y.Doc): Edit {
  const meta = doc.getMap(ROOT_META);
  const sources: Record<string, Source> = {};
  for (const [id, s] of doc.getMap(ROOT_SOURCES).entries()) sources[id] = s as Source;

  const tracks = [...doc.getMap(ROOT_TRACKS).entries()]
    .map(([id, m]) => {
      const { ord, track } = matTrack(id, m as YMap);
      return { ord, id, track };
    })
    .sort(byOrdThenId)
    .map((t) => t.track);

  return {
    type: 'fabled-edit',
    version: (meta.get('version') as 2 | undefined) ?? 2,
    settings: (meta.get('settings') as EditSettings | undefined) ?? emptyEdit().settings,
    sources,
    tracks,
  };
}

/**
 * Copy runtime-resolved pandora media urls from a previous snapshot onto a
 * freshly materialized one (the doc never carries them). Mutates `into`'s
 * source entries by replacement, not in place.
 */
export function carrySourceUrls(from: Edit, into: Edit): void {
  for (const [id, src] of Object.entries(into.sources)) {
    if (src.kind !== 'pandora-video' || src.url !== null) continue;
    const prior = from.sources[id];
    if (prior && prior.kind === 'pandora-video' && prior.url !== null) {
      into.sources[id] = { ...src, url: prior.url };
    }
  }
}
